import {
  CUSTOM_INVITE_CODE_SUFFIX_MAX_LENGTH,
  CUSTOM_INVITE_CODE_SUFFIX_MIN_LENGTH,
  inviteCodePrefixForBrand,
  normalizeCustomInviteCode,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import type { I18nKey } from '../i18n/index.ts';
import type { TParams } from '../i18n/types.ts';

/**
 * Client-side check for an optional custom invite-code suffix.
 *
 * The brand prefix is fixed (`inviteCodePrefixForBrand`) and is not part of
 * what the operator types. An empty box means "let the server generate".
 * Anything else is composed as `<PREFIX>-<suffix>` and run through
 * `normalizeCustomInviteCode`, which is the same gate the API uses — a value
 * this rejects is one the server would answer with `INVITE_CODE_FORMAT_INVALID`.
 *
 * Pasting a full code is tolerated once: if the trimmed, upper-cased input
 * already starts with `<PREFIX>-`, that head is stripped before composing so
 * `NXT-UPKING` typed into an NXT field does not become `NXT-NXT-UPKING`.
 */

export type ComposeCustomInviteCodeResult =
  | { readonly kind: 'empty' }
  | { readonly kind: 'ok'; readonly code: string }
  | { readonly kind: 'invalid' };

export function composeCustomInviteCode(
  suffix: string,
  brand: string,
): ComposeCustomInviteCodeResult {
  const trimmed = suffix.trim();
  if (trimmed.length === 0) {
    return { kind: 'empty' };
  }
  const prefix = inviteCodePrefixForBrand(brand);
  const head = `${prefix}-`;
  const withoutRepeatedPrefix = trimmed.toUpperCase().startsWith(head)
    ? trimmed.slice(head.length)
    : trimmed;
  const code = normalizeCustomInviteCode(`${prefix}-${withoutRepeatedPrefix.trim()}`, brand);
  if (code === null) {
    return { kind: 'invalid' };
  }
  return { kind: 'ok', code };
}

/** The fixed `<PREFIX>-` shown beside the suffix box, or null when no brand is known yet. */
export function inviteCodePrefixLabel(brand: string | null): string | null {
  if (brand === null || brand.length === 0) {
    return null;
  }
  return `${inviteCodePrefixForBrand(brand)}-`;
}

/**
 * Text after the first `-`. Generated codes are `<PREFIX>-XXXX-XXXX`, so the
 * rename box prefills everything the operator may edit and leaves the prefix
 * on the label.
 */
export function inviteCodeSuffixFromCode(code: string): string {
  const dash = code.indexOf('-');
  if (dash < 0) {
    return code;
  }
  return code.slice(dash + 1);
}

type Translate = (key: I18nKey, params?: TParams) => string;

/**
 * Localized copy for the two custom-text error codes. Returns null for every
 * other failure so the caller keeps its existing `coerceErrorMessage` path.
 */
export function describeInviteCodeTextError(
  error: unknown,
  t: Translate,
  params: { readonly code: string; readonly brand: string; readonly takenKey: I18nKey },
): string | null {
  if (!(error instanceof OpsApiError)) {
    return null;
  }
  if (error.code === 'INVITE_CODE_TAKEN') {
    return t(params.takenKey, { code: params.code });
  }
  if (error.code === 'INVITE_CODE_FORMAT_INVALID') {
    return t('invite_codes.custom_code.error.format', {
      prefix: inviteCodePrefixForBrand(params.brand),
      min: CUSTOM_INVITE_CODE_SUFFIX_MIN_LENGTH,
      max: CUSTOM_INVITE_CODE_SUFFIX_MAX_LENGTH,
    });
  }
  return null;
}

export function formatInviteCodeFormatError(t: Translate, brand: string): string {
  return t('invite_codes.custom_code.error.format', {
    prefix: inviteCodePrefixForBrand(brand),
    min: CUSTOM_INVITE_CODE_SUFFIX_MIN_LENGTH,
    max: CUSTOM_INVITE_CODE_SUFFIX_MAX_LENGTH,
  });
}
